import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { get, post, del, put } from '../services/apiService';
import { getFullUrl } from '../config/apiConfig';
import RichTextEditor from '../components/RichTextEditor';
import { 
  Users, 
  BookOpen, 
  DollarSign, 
  TrendingUp, 
  Plus,
  Shield,
  LogOut,
  BarChart3,
  FileText,
  Save,
  Trash2,
  Eye,
  Calendar,
  User,
  Tag,
  Clock
} from 'lucide-react';
import logger from '../utils/logger';

const AdminPanel = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('statistics'); // 'statistics', 'questions', 'blogs'
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalExams: 0,
    totalRevenue: 0,
    activeUsers: 0
  });
  const [recentUsers, setRecentUsers] = useState([]);
  const [showCreateQuestion, setShowCreateQuestion] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [examSetAvailability, setexamSetAvailability] = useState(null);
  const [selectedExamSet, setSelectedExamSet] = useState(null); // Store selected exam set
  const [bulkQuestions, setBulkQuestions] = useState([]); // Store multiple questions for bulk insert
  const [editingBulkQuestionIndex, setEditingBulkQuestionIndex] = useState(null); // Index in bulkQuestions when editing, else null
  const [deletedQuestions, setDeletedQuestions] = useState([]); // Track deleted questions
  const [apiResponse, setApiResponse] = useState(null); // Track API response
  const [showApiResponse, setShowApiResponse] = useState(false); // Control API response display
  const [rawApiResponse, setRawApiResponse] = useState(null); // Store raw API response for debug
  const [bulkUploadProgress, setBulkUploadProgress] = useState({}); // Track upload progress
  const [isBulkUploading, setIsBulkUploading] = useState(false); // Track bulk upload status
  const initialQuestionForm = {
    grade: '',
    packageType: '',
    questionTopic: '',
    setNumber: '',
    questionType: '',
    questionText: '',
    options: ['', '', '', ''],
    correctAnswer: '',
    explanation: '',
    questionImage: null,
    answerImages: [null, null, null, null],
    questionImageName: '',
    answerImageNames: ['', '', '', ''],
    answerExplanations: ['', '', '', '']
  };

  const [questionForm, setQuestionForm] = useState(initialQuestionForm);

  const resetQuestionForm = useCallback((keepExamSet = false) => {
    if (keepExamSet && selectedExamSet) {
      setQuestionForm({
        ...initialQuestionForm,
        grade: `GRADE#${selectedExamSet.grade}`,
        packageType: selectedExamSet.package || '',
        questionTopic: selectedExamSet.type || '',
        setNumber: String(selectedExamSet.setNumber),
      });
    } else {
      setQuestionForm(initialQuestionForm);
    }
  }, [selectedExamSet]);

  // Blog state
  const [blogs, setBlogs] = useState([]);
  const [showBlogEditor, setShowBlogEditor] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [blogForm, setBlogForm] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    metaTitle: '',
    metaDescription: '',
    tags: '',
    status: 'draft',
    featuredImage: null,
    featuredImageName: ''
  });

  // Custom confirmation dialog function
  const showConfirmDialog = (message) => {
    return window.confirm(message);
  };

  // Warn on page refresh or tab close
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (bulkQuestions.length > 0) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes in the question editor. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [bulkQuestions.length]);

  // Warn on browser back/forward navigation (popstate)
  useEffect(() => {
    const hasUnsavedChanges = bulkQuestions.length > 0;
    if (!hasUnsavedChanges) return;

    // Push a dummy state to history so we have something to pop
    window.history.pushState({ noBack: true }, "");

    const handlePopState = (event) => {
      if (!window.confirm("You have unsaved changes in the question editor. Are you sure you want to leave?")) {
        // User cancelled, push the state back to keep blocking
        window.history.pushState({ noBack: true }, "");
      } else {
        // User confirmed, clear state and go back
        setBulkQuestions([]);
        window.history.back();
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [bulkQuestions.length > 0]);

  // Check if user is admin
  useEffect(() => {
    const checkAdminAccess = () => {
      const idToken = user?.idToken || user?.session?.idToken;
      
      if (!idToken) {
        navigate('/dashboard');
        return;
      }

      const groups = idToken?.payload?.["cognito:groups"] || [];
      const isAdmin = Array.isArray(groups) && groups.includes('admin');

      if (!isAdmin) {
         navigate('/dashboard');
        return;
      }

      setLoading(false);
    };

    if (user) checkAdminAccess();
  }, [user, navigate]);

  // Fetch admin data
  useEffect(() => {
    if (!loading) {
      fetchAdminData();
    }
  }, [loading]);

  const fetchAdminData = async () => {
    try {
      // Fetch blogs - use /admin/blogs to get all blogs including unpublished
      const blogsResponse = await get(getFullUrl('admin/blogs'));
      
      let blogsData;
      try {
        const jsonResponse = await blogsResponse.json();
       
        // Handle the nested structure: { success: true, data: { blogs: [...], total: 2 } }
        if (jsonResponse.success && jsonResponse.data) {
          // Handle both structures: { data: { blogs: [...] } } and { data: [] }
          if (Array.isArray(jsonResponse.data.blogs)) {
            blogsData = jsonResponse.data.blogs;
          } else if (Array.isArray(jsonResponse.data)) {
            blogsData = jsonResponse.data;
          } else {
            blogsData = [];
          }
          
          // Map blog data structure to match what our component expects
          blogsData = blogsData.map(blog => ({
            ...blog,
            // Convert published boolean to status string
            status: blog.published ? 'published' : 'draft',
            // Handle multiple possible content field names
            content: blog.content || blog.body || blog.text || blog.description || blog.articleContent || '',
            // Handle excerpt field
            excerpt: blog.excerpt || blog.description || '',
            // Handle tags - ensure it's an array
            tags: Array.isArray(blog.tags) ? blog.tags : (blog.tags ? blog.tags.split(',').map(tag => tag.trim()) : []),
            // Handle author field
            author: blog.author || 'Admin'
          }));
       } else {
          blogsData = Array.isArray(jsonResponse) ? jsonResponse : [];
        }
      } catch (error) {
        const htmlContent = await blogsResponse.text();
        // For admin panel, if we get HTML, we'll create an empty array
        blogsData = [];
      }
      
      // Always ensure blogsData is an array before setting
      if (Array.isArray(blogsData)) {
        setBlogs(blogsData);
      } else {
        setBlogs([]);
      }

      // const res = await get('getFullUrl('questions');

      // if (res.ok) {
      //   const data = await res.json();
      //   // setQuestions(data); // Removed since questions state was removed
      // }

      // // Mock stats
      // setStats({
      //   totalUsers: 1250,
      //   totalExams: 45,
      //   totalRevenue: 15420,
      //   activeUsers: 892
      // });

      // setRecentUsers([
      //   { id: 1, name: 'Sarah', email: 'sarah@email.com', status: 'active' },
      //   { id: 2, name: 'Mike', email: 'mike@email.com', status: 'active' }
      // ]);

    } catch (err) {
      logger.error(err);
    }
  };

  const handleLogout = () => {
    if (bulkQuestions.length > 0) {
      if (!window.confirm("You have unsaved changes in the question editor. Are you sure you want to log out and lose your changes?")) {
        return;
      }
    }
    setBulkQuestions([]); // Clear state to disable blocker
    logout();
    navigate('/');
  };

  // Blog functions
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleBlogTitleChange = (value) => {
    setBlogForm(prev => ({
      ...prev,
      title: value,
      // Only generate slug if not editing (new blog)
      slug: editingBlog ? prev.slug : generateSlug(value),
      metaTitle: value || prev.metaTitle
    }));
  };

  const handleBlogContentChange = (content) => {
    setBlogForm(prev => ({
      ...prev,
      content,
      excerpt: content.replace(/<[^>]*>/g, '').substring(0, 160) + '...'
    }));
  };

  const handleBlogImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setBlogForm(prev => ({
          ...prev,
          featuredImage: e.target.result,
          featuredImageName: file.name
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeBlogImage = () => {
    setBlogForm(prev => ({
      ...prev,
      featuredImage: null,
      featuredImageName: ''
    }));
  };

  const saveBlog = async () => {
    try {
      const blogData = {
        ...blogForm,
        published: blogForm.status === 'published', // Convert status to boolean
        author: user.email,
        createdAt: editingBlog ? editingBlog.createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      let response;
      if (editingBlog) {
        // Use PUT method with update endpoint for editing
             response = await put(getFullUrl(`admin/update-blog/${editingBlog.slug}`), blogData);
      } else {
        // Use POST method for creating new blog
        // Debug log for required fields
        response = await post(getFullUrl('admin/blog'), blogData);
      }
    
      // Handle both JSON and HTML responses
      let responseData;
      try {
        responseData = await response.json();
      } catch (error) {
        responseData = await response.text();
        // For HTML responses, we'll consider it successful if the response is OK
        responseData = { success: response.ok };
      }

      if (responseData || response.ok) {
        await fetchAdminData();
        resetBlogForm();
        setShowBlogEditor(false);
      }
    } catch (error) {
      console.error('Failed to save blog:', error);
    }
  };

  const deleteBlog = async (blogSlug) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) {
      return;
    }

    try {
      const response = await del(getFullUrl(`admin/delete-blog/${blogSlug}`));
      
      // Handle both JSON and HTML responses
      let responseData;
      try {
        responseData = await response.json();
      } catch (error) {
        responseData = await response.text();
        // For HTML responses, we'll consider it successful if the response is OK
        responseData = { success: response.ok };
      }
      
      if (responseData || response.ok) {
        await fetchAdminData();
      }
    } catch (error) {
      console.error('Failed to delete blog:', error);
    }
  };

  const editBlog = async (blog) => {
   
    try {
      // Use the edit endpoint to get full blog data including content
      const response = await get(getFullUrl(`admin/blog/${blog.slug}/edit`));
      
      let blogData;
      try {
        const jsonResponse = await response.json();
        
        // Handle the nested structure: { success: true, data: { blog } }
        if (jsonResponse.success && jsonResponse.data) {
          blogData = jsonResponse.data;
        } else {
          blogData = jsonResponse;
        }
      } catch (error) {
        const htmlContent = await response.text();
        // Fallback to original blog data if we get HTML
        blogData = blog;
      }
      
      // Set the blog form with the full data
      setEditingBlog(blogData);
      setBlogForm({
        title: blogData.title || '',
        slug: blogData.slug || '',
        content: blogData.content || '',
        excerpt: blogData.excerpt || '',
        metaTitle: blogData.metaTitle || blogData.title || '',
        metaDescription: blogData.metaDescription || blogData.excerpt || '',
        tags: blogData.tags || [],
        status: blogData.published ? 'published' : 'draft',
        featuredImage: blogData.featuredImage || '',
        featuredImageName: blogData.featuredImageName || ''
      });
      setShowBlogEditor(true);
      
    } catch (error) {
      console.error('Failed to fetch blog data for editing:', error);
      // Fallback to original blog data
      setEditingBlog(blog);
      setBlogForm({
        title: blog.title || '',
        slug: blog.slug || '',
        content: blog.content || '',
        excerpt: blog.excerpt || '',
        metaTitle: blog.metaTitle || blog.title || '',
        metaDescription: blog.metaDescription || blog.excerpt || '',
        tags: blog.tags || [],
        status: blog.status || 'draft',
        featuredImage: blog.featuredImage || '',
        featuredImageName: blog.featuredImageName || ''
      });
      setShowBlogEditor(true);
    }
  };

  const resetBlogForm = () => {
    setBlogForm({
      title: '',
      slug: '',
      content: '',
      excerpt: '',
      metaTitle: '',
      metaDescription: '',
      tags: '',
      status: 'draft',
      featuredImage: null,
      featuredImageName: ''
    });
    setEditingBlog(null);
  };

  // Check NEP set availability in real-time
  const checkexamSetAvailability = useCallback(async () => {
    if (!questionForm.grade || !questionForm.packageType || !questionForm.questionTopic || !questionForm.setNumber) {
      setexamSetAvailability(null);
      return;
    }

    try {
       const apiUrl = getFullUrl(`examset?grade=${questionForm.grade.replace('GRADE#', '')}&package=${questionForm.packageType}&examType=${questionForm.questionTopic}&setNumber=${questionForm.setNumber}`);
        // const apiUrl = `http://localhost:3001/dev/examset?grade=${questionForm.grade.replace('GRADE#', '')}&package=${questionForm.packageType}&examType=${questionForm.questionTopic}&setNumber=${questionForm.setNumber}`;
    
      
      

      const response = await get(apiUrl);
      
      // Try to get the response body - handle ReadableStream
      let responseBody = null;
      if (response) {
        try {
          // Check if response has a body that's a ReadableStream
          if (response.body && response.body instanceof ReadableStream) {
            
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let result = '';
            
            try {
              while (true) {
                const { done, value } = await reader.read();
               
                if (done) break;
                result += decoder.decode(value, { stream: true });
              }
              
             
              responseBody = JSON.parse(result);
             } catch (streamError) {
              throw streamError;
            }
          } else if (typeof response === 'string') {
            responseBody = JSON.parse(response);
          } else if (response.body && typeof response.body === 'string') {
            responseBody = JSON.parse(response.body);
          } else if (response.body && typeof response.body === 'object') {
            responseBody = response.body;
          } else if (response.success !== undefined || response.available !== undefined) {
            responseBody = response;
          } else {
            if (typeof response === 'object' && response !== null) {
              try {
                const stringified = JSON.stringify(response);
                responseBody = JSON.parse(stringified);
              } catch (stringifyError) {
               }
            }
          }
        } catch (parseError) {
           responseBody = response;
        }
      }
      
      // Check availability based on the parsed response
      let parsedData = null;
      
      if (responseBody && responseBody.body && typeof responseBody.body === 'string') {
        try {
          parsedData = JSON.parse(responseBody.body);
        } catch (parseError) {
          parsedData = responseBody;
        }
      } else if (responseBody && responseBody.body && typeof responseBody.body === 'object') {
        parsedData = responseBody.body;
      } else if (responseBody) {
        parsedData = responseBody;
      }
      
      if (parsedData && parsedData.success && parsedData.available) {
        
        setexamSetAvailability({
          available: true,
          message: parsedData.message || 'Exam set is available',
          examSet: parsedData.examSet || parsedData.available,
          existingQuestions: parsedData.examQuestions || []
        });
      } else {
        
        setexamSetAvailability({
          available: false,
          message: parsedData?.message || 'Exam set not found',
          examSet: null,
          existingQuestions: []
        });
      }
    } catch (error) {
      setexamSetAvailability({
        available: false,
        message: 'Error checking exam set availability',
        examSet: null,
        existingQuestions: []
      });
    }
  }, [questionForm.grade, questionForm.packageType, questionForm.questionTopic, questionForm.setNumber]);

  // Update options array when question type changes
  useEffect(() => {
    if (questionForm.questionType === 'MULTI_SELECT' && questionForm.options.length < 5) {
      setQuestionForm(prev => ({
        ...prev,
        options: [...prev.options, '', ''].slice(0, 5),
        answerImages: [...prev.answerImages, null, null].slice(0, 5),
        answerImageNames: [...prev.answerImageNames, '', ''].slice(0, 5)
      }));
    } else if (questionForm.questionType === 'MCQ' && questionForm.options.length > 4) {
      setQuestionForm(prev => ({
        ...prev,
        options: prev.options.slice(0, 4),
        answerImages: prev.answerImages.slice(0, 4),
        answerImageNames: prev.answerImageNames.slice(0, 4)
      }));
    }
  }, [questionForm.questionType]);

  // Question creation handlers
  const handleQuestionFormChange = (field, value) => {
    setQuestionForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAnswerExplanationChange = (index, value) => {
    setQuestionForm(prev => {
      const newAnswerExplanations = [...(prev.answerExplanations || ['', '', '', '', '', ''])];
      newAnswerExplanations[index] = value;
      return {
        ...prev,
        answerExplanations: newAnswerExplanations
      };
    });
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...questionForm.options];
    newOptions[index] = value;
    setQuestionForm(prev => ({
      ...prev,
      options: newOptions
    }));
  };

  // Generate unique filename
  const generateUniqueFileName = (originalFileName, prefix = '') => {
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substr(2, 9);
    const fileExtension = originalFileName.split('.').pop().toLowerCase();
    // Remove extension from original filename before sanitizing
    const nameWithoutExtension = originalFileName.substring(0, originalFileName.lastIndexOf('.'));
    const sanitizedName = nameWithoutExtension.replace(/[^a-zA-Z0-9.-]/g, '_').substr(0, 20);
    return `${prefix}${sanitizedName}_${timestamp}_${randomString}.${fileExtension}`;
  };

  const handleQuestionImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const uniqueFileName = generateUniqueFileName(file.name, 'question_');
      const reader = new FileReader();
      reader.onload = (e) => {
        setQuestionForm(prev => ({
          ...prev,
          questionImage: e.target.result,
          questionImageName: uniqueFileName
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAnswerImageUpload = (index, event) => {
    const file = event.target.files[0];
    if (file) {
      const uniqueFileName = generateUniqueFileName(file.name, `answer${index + 1}_`);
      const reader = new FileReader();
      reader.onload = (e) => {
        const newAnswerImages = [...questionForm.answerImages];
        newAnswerImages[index] = e.target.result;
        const newAnswerImageNames = [...questionForm.answerImageNames];
        newAnswerImageNames[index] = uniqueFileName;
        setQuestionForm(prev => ({
          ...prev,
          answerImages: newAnswerImages,
          answerImageNames: newAnswerImageNames
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeQuestionImage = () => {
    setQuestionForm(prev => ({
      ...prev,
      questionImage: null,
      questionImageName: ''
    }));
  };

  const removeAnswerImage = (index) => {
    const newAnswerImages = [...questionForm.answerImages];
    newAnswerImages[index] = null;
    const newAnswerImageNames = [...questionForm.answerImageNames];
    newAnswerImageNames[index] = '';
    setQuestionForm(prev => ({
      ...prev,
      answerImages: newAnswerImages,
      answerImageNames: newAnswerImageNames
    }));
  };

  /** Map a bulk list question into questionForm state for the create/edit wizard */
  const bulkQuestionToQuestionForm = (q) => {
    const maxOpts = q.questionType === 'MULTI_SELECT' ? 5 : 4;
    const rawOpts = Array.isArray(q.options) ? [...q.options] : [];
    while (rawOpts.length < maxOpts) rawOpts.push('');
    if (rawOpts.length > maxOpts) rawOpts.length = maxOpts;

    const letterCsvToIndexCsv = (str) => {
      if (str === undefined || str === null) return '';
      const s = String(str).trim();
      if (!s) return '';
      const parts = s.split(',').map((x) => x.trim()).filter(Boolean);
      if (!parts.length) return '';
      if (parts.every((p) => /^[A-Z]$/i.test(p))) {
        return parts.map((p) => p.toUpperCase().charCodeAt(0) - 65).join(',');
      }
      return parts.join(',');
    };

    let correctAnswer = q.correctAnswer;
    if (q.questionType === 'MCQ') {
      if (typeof correctAnswer === 'number') {
        correctAnswer = String(correctAnswer);
      } else {
        correctAnswer = letterCsvToIndexCsv(correctAnswer);
      }
    } else if (q.questionType === 'MULTI_SELECT' && typeof correctAnswer === 'string') {
      correctAnswer = letterCsvToIndexCsv(correctAnswer);
    } else if (correctAnswer !== undefined && correctAnswer !== null) {
      correctAnswer = String(correctAnswer);
    } else {
      correctAnswer = '';
    }

    let answerExplanations = Array(maxOpts).fill('');
    if (q.answerExplanations) {
      if (Array.isArray(q.answerExplanations)) {
        q.answerExplanations.forEach((val, i) => {
          if (i < maxOpts) answerExplanations[i] = val || '';
        });
      } else if (typeof q.answerExplanations === 'object') {
        for (let i = 0; i < maxOpts; i++) {
          const key = String.fromCharCode(65 + i);
          answerExplanations[i] = q.answerExplanations[key] || '';
        }
      }
    }

    const answerImages = Array(maxOpts).fill(null);
    const answerImageNames = Array(maxOpts).fill('');
    if (Array.isArray(q.answerImages)) {
      q.answerImages.forEach((img, i) => {
        if (i < maxOpts) answerImages[i] = img;
      });
    }
    if (Array.isArray(q.answerImageNames)) {
      q.answerImageNames.forEach((name, i) => {
        if (i < maxOpts) answerImageNames[i] = name || '';
      });
    }

    const es = selectedExamSet;
    return {
      grade: es ? `GRADE#${es.grade}` : '',
      packageType: es?.package || '',
      questionTopic: es?.type || '',
      setNumber: es != null ? String(es.setNumber) : '',
      questionType: q.questionType || '',
      questionText: q.questionText || '',
      options: rawOpts,
      correctAnswer,
      explanation: q.explanation || '',
      answerExplanations,
      questionImage: q.questionImage ?? null,
      questionImageName: q.questionImageName || '',
      answerImages,
      answerImageNames
    };
  };

  const handleCreateQuestion = async () => {
    try {
      // If no exam set is selected, this is exam set selection phase
      if (!selectedExamSet) {
        // Check if exam set exists before selecting
        if (!questionForm.grade || !questionForm.packageType || !questionForm.questionTopic || !questionForm.setNumber) {
          return; // Don't show alert, just return
        }

        const examSetCheckResponse = await get(
          getFullUrl(`examset?grade=${questionForm.grade.replace('GRADE#', '')}&package=${questionForm.packageType}&examType=${questionForm.questionTopic}&setNumber=${questionForm.setNumber}`)
        );

        
        // Parse the response - handle ReadableStream
        let parsedData = null;
        let responseBody = null;
        
        if (examSetCheckResponse && examSetCheckResponse.body && examSetCheckResponse.body instanceof ReadableStream) {
           const reader = examSetCheckResponse.body.getReader();
          const decoder = new TextDecoder();
          let result = '';
          
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            result += decoder.decode(value, { stream: true });
          }
          
          responseBody = JSON.parse(result);
        } else if (examSetCheckResponse && examSetCheckResponse.body && typeof examSetCheckResponse.body === 'string') {
          try {
            parsedData = JSON.parse(examSetCheckResponse.body);
           } catch (parseError) {
             parsedData = examSetCheckResponse;
          }
        } else if (examSetCheckResponse && examSetCheckResponse.body && typeof examSetCheckResponse.body === 'object') {
          parsedData = examSetCheckResponse.body; 
        } else if (examSetCheckResponse && (examSetCheckResponse.success !== undefined || examSetCheckResponse.available !== undefined)) {
          parsedData = examSetCheckResponse; 
        } else if (examSetCheckResponse) {
          // Try to parse the response directly
          try {
            if (typeof examSetCheckResponse === 'string') {
              parsedData = JSON.parse(examSetCheckResponse);
            } else {
              const stringified = JSON.stringify(examSetCheckResponse);
              parsedData = JSON.parse(stringified);
            }
            
          } catch (parseError) {
            parsedData = examSetCheckResponse;
          }
        }
        
        // If we got a responseBody from ReadableStream, parse the body property
        if (responseBody) {
          if (responseBody.body && typeof responseBody.body === 'string') {
            try {
              parsedData = JSON.parse(responseBody.body); 
            } catch (parseError) {
              parsedData = responseBody;
            }
          } else if (responseBody.body && typeof responseBody.body === 'object') {
            parsedData = responseBody.body; 
          } else {
            parsedData = responseBody;
           }
        }

        // Check if exam set is available
        if (!parsedData || !parsedData.success || !parsedData.available) {
          // Don't show alert, just close the modal and let user try again
          setShowCreateQuestion(false);
          return;
        }

        // Select the exam set 
        setSelectedExamSet(parsedData.examSet);
        
        // Set existing questions if any
        // Handle different API response structures
        let examSetsData = []; 
        
        if (Array.isArray(parsedData)) {
          examSetsData = parsedData; 
        } else if (parsedData.examQuestions && Array.isArray(parsedData.examQuestions)) {
          examSetsData = parsedData.examQuestions; 
        } else if (parsedData.questions && Array.isArray(parsedData.questions)) {
          examSetsData = [parsedData]; // Single exam set with questions
          
        } else {
          
        }
        
        if (examSetsData.length > 0) {
         
          // Transform existing questions to our format
          const transformedQuestions = [];
          
          // Handle the case where examSetsData contains questions directly
          if (examSetsData.length > 0 && examSetsData[0].questionId) {
             examSetsData.forEach((question, questionIndex) => {
              
              // Transform options to text array
              const optionsText = question.options ? question.options.map(opt => opt?.text || '').filter(text => text !== '') : [];
              
              // Get explanation for correct answer
              let explanation = '';
              // For SHORT_ANSWER and LONG_WRITING, use singular 'explanation' field
              if (question.questionType === 'SHORT_ANSWER' || question.questionType === 'LONG_WRITING') {
                explanation = question.explanation || '';
              } else if (question.explanations && question.correctAnswer) {
                // For MCQ/MULTI_SELECT, use plural 'explanations' object with answer keys
                const answerKey = question.correctAnswer.split(',')[0].trim(); // Take first answer for main explanation
                explanation = question.explanations[answerKey] || '';
              }
              
              // Normalize correctAnswer format
              let normalizedCorrectAnswer = question.correctAnswer;
              if (typeof question.correctAnswer === 'string') {
                // If it's already a string like "A" or "A,B", keep it as is
                normalizedCorrectAnswer = question.correctAnswer;
              } else if (Array.isArray(question.correctAnswer)) {
                // If it's an array like [0, 1], convert to letters
                normalizedCorrectAnswer = question.correctAnswer.map(idx => String.fromCharCode(65 + parseInt(idx))).join(',');
              } else if (typeof question.correctAnswer === 'number') {
                // If it's a single number like 0, convert to letter
                normalizedCorrectAnswer = String.fromCharCode(65 + parseInt(question.correctAnswer));
              }
              
              const transformedQuestion = {
                id: question.questionId,
                questionType: question.questionType,
                questionText: question.question,
                options: optionsText,
                correctAnswer: normalizedCorrectAnswer, // Always letter format
                explanation: explanation,
                answerExplanations: question.explanations || {}, // Keep as object
                questionImage: question.questionImage || null,
                answerImages: question.answerImages || [],
                existing: true, // Mark as existing question
                examId: question.examId || 'existing',
                createdBy: question.createdBy || 'unknown',
                examSetNumber: question.examSetNumber || '1',
                examType: question.examType || 'UNKNOWN',
                grade: question.grade || '5',
                package: question.package || 'PKG_STANDARD'
              };
             
              transformedQuestions.push(transformedQuestion);
            });
          } else {
             examSetsData.forEach((exam, examIndex) => {
              
              // Handle different structures - exam might be the questions array directly
              let questionsToProcess = [];
              if (exam.questions && Array.isArray(exam.questions)) {
                questionsToProcess = exam.questions;
               } else if (Array.isArray(exam)) {
                questionsToProcess = exam;
               } else {
                return;
              }
              
              questionsToProcess.forEach((question, questionIndex) => {
                // Transform options to text array
                const optionsText = question.options ? question.options.map(opt => opt?.text || '').filter(text => text !== '') : [];
                
                // Get explanation for correct answer
                let explanation = '';
                // For SHORT_ANSWER and LONG_WRITING, use singular 'explanation' field
                if (question.questionType === 'SHORT_ANSWER' || question.questionType === 'LONG_WRITING') {
                  explanation = question.explanation || '';
                } else if (question.explanations && question.correctAnswer) {
                  // For MCQ/MULTI_SELECT, use plural 'explanations' object with answer keys
                  const answerKey = question.correctAnswer.split(',')[0].trim(); // Take first answer for main explanation
                  explanation = question.explanations[answerKey] || '';
                }
                
                // Normalize correctAnswer format
                let normalizedCorrectAnswer = question.correctAnswer;
                if (typeof question.correctAnswer === 'string') {
                  // If it's already a string like "A" or "A,B", keep it as is
                  normalizedCorrectAnswer = question.correctAnswer;
                } else if (Array.isArray(question.correctAnswer)) {
                  // If it's an array like [0, 1], convert to letters
                  normalizedCorrectAnswer = question.correctAnswer.map(idx => String.fromCharCode(65 + parseInt(idx))).join(',');
                } else if (typeof question.correctAnswer === 'number') {
                  // If it's a single number like 0, convert to letter
                  normalizedCorrectAnswer = String.fromCharCode(65 + parseInt(question.correctAnswer));
                }
                
                const transformedQuestion = {
                  id: question.questionId,
                  questionType: question.questionType,
                  questionText: question.question,
                  options: optionsText,
                  correctAnswer: normalizedCorrectAnswer, // Always letter format
                  explanation: explanation,
                  answerExplanations: question.explanations || {}, // Keep as object
                  questionImage: question.questionImage || null,
                  answerImages: question.answerImages || [],
                  existing: true, // Mark as existing question
                  examId: exam.examId,
                  createdBy: exam.createdBy,
                  examSetNumber: exam.examSetNumber,
                  examType: exam.examType,
                  grade: exam.grade,
                  package: exam.package
                };
                 
                transformedQuestions.push(transformedQuestion);
              });
            });
          }
          
           setBulkQuestions(transformedQuestions);
          
          // Debug: Check if state is set correctly
           
        } else {
           setBulkQuestions([]);
        }
        
        setQuestionForm({
          ...questionForm,
          grade: parsedData.examSet.grade ? `GRADE#${parsedData.examSet.grade}` : questionForm.grade,
          packageType: parsedData.examSet.package || questionForm.packageType,
          questionTopic: parsedData.examSet.type || questionForm.questionTopic,
          setNumber: parsedData.examSet.setNumber || questionForm.setNumber,
        });
        
        // Close modal and show the selected exam set
        setShowCreateQuestion(false);
        setCurrentStep(1);
        return;
      }

      // This is question creation phase
      // Validate form
      if (!questionForm.questionType || !questionForm.questionText) {
        return; // Don't show alert, just return
      }

      if (questionForm.questionType === 'MCQ' && questionForm.options.filter(opt => opt.trim()).length < 2) {
        return; // Don't show alert, just return
      }

      if (questionForm.questionType === 'MULTI_SELECT' && questionForm.options.filter(opt => opt.trim()).length < 5) {
        return; // Don't show alert, just return
      }

      if (editingBulkQuestionIndex === null && bulkQuestions.length >= 40) {
        return; // Don't show alert, just return
      }

      const sharedFields = {
        grade: questionForm.grade,
        packageType: questionForm.packageType,
        questionTopic: questionForm.questionTopic,
        setNumber: questionForm.setNumber,
        questionType: questionForm.questionType,
        questionText: questionForm.questionText,
        options: questionForm.options,
        correctAnswer: questionForm.correctAnswer,
        explanation: questionForm.explanation,
        answerExplanations:
          questionForm.questionType === 'MCQ' || questionForm.questionType === 'MULTI_SELECT'
            ? questionForm.answerExplanations || []
            : [],
        questionImage: questionForm.questionImage,
        answerImages: questionForm.answerImages,
        questionImageName: questionForm.questionImageName,
        answerImageNames: questionForm.answerImageNames
      };

      let questionPayload;
      if (editingBulkQuestionIndex !== null) {
        const original = bulkQuestions[editingBulkQuestionIndex];
        questionPayload = {
          ...original,
          ...sharedFields,
          id: original.id,
          existing: original.existing,
          createdBy: original.createdBy || user.email
        };
        setBulkQuestions(prev =>
          prev.map((item, idx) => (idx === editingBulkQuestionIndex ? questionPayload : item))
        );
        setEditingBulkQuestionIndex(null);
      } else {
        questionPayload = {
          ...sharedFields,
          createdBy: user.email,
          id: Date.now().toString()
        };
        setBulkQuestions(prev => [...prev, questionPayload]);
      }
      
      // Reset question form (but keep exam set info)
      resetQuestionForm(true);
      setCurrentStep(1);
      setShowCreateQuestion(false);
      
    } catch (error) {
      // Don't show alert, just log the error
    }
  };

  // S3 Upload Utility Functions
  const compressImage = async (file, maxWidth = 800, quality = 0.7) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(resolve, 'image/jpeg', quality);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const getBulkUploadPresignedUrls = async (fileRequests) => {
    try { 
      
      // Determine folder structure based on selected exam set
      const folder = selectedExamSet ? 
        `exam-images/grade${selectedExamSet.grade}/${selectedExamSet.package?.toLowerCase()}/${selectedExamSet.type?.toLowerCase()}` : 
        'exam-images';
      
      // Transform fileRequests to match expected API format
      const files = fileRequests.map(req => {
        // Calculate file size from base64 data if available
        let fileSize = 0;
        if (req.base64Data && req.base64Data.startsWith('data:')) {
          // Extract base64 part and calculate size
          const base64Content = req.base64Data.split(',')[1];
          fileSize = Math.round((base64Content.length * 3) / 4); // Base64 to bytes conversion
        }
        
        return {
          fileName: req.fileName,
          fileType: req.fileType,
          fileSize: fileSize
        };
      });
      
      const requestBody = {
        files: files,
        folder: folder
      };
      
      
      const response = await fetch(getFullUrl('upload/bulkupload-presigned-urls'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user?.session?.idToken.jwtToken}`
        },
        body: JSON.stringify(requestBody)
      });
      
      
      if (!response.ok) {
        const errorText = await response.text();
         throw new Error(`Failed to get presigned URLs: ${response.status} - ${errorText}`);
      }
      
      const responseText = await response.text();
      
      let responseData;
      try {
        // First, try to parse as normal JSON
        const parsedResponse = JSON.parse(responseText);
        
        // Check if the response has the structure {statusCode, headers, body}
        if (parsedResponse.body && typeof parsedResponse.body === 'string') {
           // Parse the body string which contains the actual JSON
          responseData = JSON.parse(parsedResponse.body);
        } else {
          // Use the parsed response directly
          responseData = parsedResponse;
        }
      } catch (parseError) {
        
        // Try to parse if it's wrapped in quotes
        if (responseText.startsWith('"') && responseText.endsWith('"')) {
          try {
            responseData = JSON.parse(responseText.slice(1, -1));
          } catch (secondParseError) {
            throw new Error(`Failed to parse API response: ${secondParseError.message}`);
          }
        } else {
          throw new Error(`Failed to parse API response: ${parseError.message}`);
        }
      }
      
       return responseData;
    } catch (error) {
      throw error;
    }
  };

 const uploadFileToS3 = async (file, presignedUrl, onProgress = null) => {
  return new Promise((resolve, reject) => {
    if (!file || !presignedUrl) {
      reject(new Error("Missing file or presigned URL"));
      return;
    }

     

    const xhr = new XMLHttpRequest();

    // ✅ Progress tracking
    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(percent);
        }
      };
    }

    // ✅ Success handler
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
         
        // Remove query params → clean file URL
        const cleanUrl = presignedUrl.split("?")[0];

        resolve(cleanUrl);
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    };

    // ❌ Network / CORS error
    xhr.onerror = () => {
      reject(new Error("Upload failed (network/CORS issue)"));
    };

    // ❌ Abort handling
    xhr.onabort = () => {
       reject(new Error("Upload aborted"));
    };

    // ✅ IMPORTANT: PUT request (required for presigned URL)
    xhr.open("PUT", presignedUrl);

    // ✅ Only required header
    xhr.setRequestHeader("Content-Type", file.type);

    try {
      xhr.send(file);
    } catch (err) {
      reject(err);
    }
  });
};

  const convertBase64ToFile = async (base64String, filename) => {
    try {
      // Extract the base64 data (remove data:image/...;base64, prefix)
      const base64Data = base64String.split(',')[1];
      const mimeType = base64String.split(',')[0].split(':')[1].split(';')[0];
      
      // Convert base64 to blob
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });
      
      // Create file object
      return new File([blob], filename, { type: mimeType });
    } catch (error) {
       throw error;
    }
  };

  // Handle bulk submit
  const handleBulkSubmit = async () => {
    try {
      if (bulkQuestions.length === 0) {
        return; // Don't show alert, just return
      }

      setIsBulkUploading(true);
      setBulkUploadProgress({});
      
       
      // Step 1: Collect all images that need to be uploaded
      const fileRequests = [];
      const questionImageMap = new Map(); // Maps questionIndex to image info
      const answerImageMap = new Map(); // Maps "questionIndex-answerIndex" to image info
      
      bulkQuestions.forEach((question, questionIndex) => {
        // Skip image processing for existing questions (they already have S3 URLs)
        if (question.existing) {
          return;
        }
        
        // Add question image if exists (only for new questions with base64 data)
        if (question.questionImage && question.questionImageName) {
          const fileKey = `question-${questionIndex}`;
          fileRequests.push({
            fileName: question.questionImageName,
            fileType: question.questionImage.startsWith('data:') ? 
              question.questionImage.split(':')[1].split(';')[0] : 'image/jpeg'
          });
          questionImageMap.set(fileKey, {
            questionIndex,
            fileName: question.questionImageName,
            base64Data: question.questionImage
          });
        }
        
        // Add answer images if they exist (only for new questions with base64 data)
        if (question.answerImages && question.answerImageNames) {
          question.answerImages.forEach((answerImage, answerIndex) => {
            if (answerImage && question.answerImageNames[answerIndex]) {
              const fileKey = `question-${questionIndex}-answer-${answerIndex}`;
              fileRequests.push({
                fileName: question.answerImageNames[answerIndex],
                fileType: answerImage.startsWith('data:') ? 
                  answerImage.split(':')[1].split(';')[0] : 'image/jpeg'
              });
              answerImageMap.set(fileKey, {
                questionIndex,
                answerIndex,
                fileName: question.answerImageNames[answerIndex],
                base64Data: answerImage
              });
            }
          });
        }
      });
      
      
      if (fileRequests.length === 0) {
         await submitQuestionsToBackend(bulkQuestions);
        return;
      }
      
      // Step 2: Get pre-signed URLs from backend
       let presignedResponse;
      try {
        presignedResponse = await getBulkUploadPresignedUrls(fileRequests);
      } catch (error) {
        throw error;
      }
      
      
      // Handle the expected response format: { success: true, data: { presignedUrls: [...] } }
      let presignedUrls = [];
      if (presignedResponse.success && presignedResponse.data && presignedResponse.data.presignedUrls) {
        presignedUrls = presignedResponse.data.presignedUrls;
      } else if (Array.isArray(presignedResponse)) {
        presignedUrls = presignedResponse;
      } else if (presignedResponse.urls && Array.isArray(presignedResponse.urls)) {
        presignedUrls = presignedResponse.urls;
      } else {
        throw new Error(`Failed to get pre-signed URLs: Unexpected response format`);
      }
      
      if (presignedUrls.length === 0) {
        throw new Error('No pre-signed URLs returned from backend');
      }
      
      
      
      // Step 3: Upload files to S3
      const uploadPromises = [];
      const s3UrlMap = new Map(); // Maps fileKey to S3 URL
      
      // Upload question images
      for (const [fileKey, imageInfo] of questionImageMap) {
        const presignedUrlInfo = presignedUrls.find(url => url.fileName === imageInfo.fileName);
        if (presignedUrlInfo && presignedUrlInfo.presignedUrl) {
          const file = await convertBase64ToFile(imageInfo.base64Data, imageInfo.fileName);
          const compressedFile = await compressImage(file);
          
          const uploadPromise = uploadFileToS3(compressedFile, presignedUrlInfo.presignedUrl, (progress) => {
             setBulkUploadProgress(prev => ({
              ...prev,
              [imageInfo.fileName]: progress
            }));
          }).then(s3Url => {
            // Use the key from response or construct from presigned URL
            const finalS3Url = presignedUrlInfo.key ? 
              `https://${presignedResponse.data.bucketName}.s3.ap-southeast-2.amazonaws.com/${presignedUrlInfo.key}` : 
              s3Url;
            s3UrlMap.set(fileKey, finalS3Url);
          });
          
          uploadPromises.push(uploadPromise);
        }
      }
      
      // Upload answer images
      for (const [fileKey, imageInfo] of answerImageMap) {
        const presignedUrlInfo = presignedUrls.find(url => url.fileName === imageInfo.fileName);
        if (presignedUrlInfo && presignedUrlInfo.presignedUrl) {
          const file = await convertBase64ToFile(imageInfo.base64Data, imageInfo.fileName);
          const compressedFile = await compressImage(file);
          
          const uploadPromise = uploadFileToS3(compressedFile, presignedUrlInfo.presignedUrl, (progress) => {
             setBulkUploadProgress(prev => ({
              ...prev,
              [imageInfo.fileName]: progress
            }));
          }).then(s3Url => {
            // Use the key from response or construct from presigned URL
            const finalS3Url = presignedUrlInfo.key ? 
              `https://${presignedResponse.data.bucketName}.s3.ap-southeast-2.amazonaws.com/${presignedUrlInfo.key}` : 
              s3Url;
            s3UrlMap.set(fileKey, finalS3Url);
          });
          
          uploadPromises.push(uploadPromise);
        }
      }
      
      // Wait for all uploads to complete
      try {
        await Promise.all(uploadPromises); 
      } catch (uploadError) {
        
        // Check if it's a CORS error
        if (uploadError.message && uploadError.message.includes('CORS')) {
          alert('S3 upload failed due to CORS policy. Please ensure your S3 bucket is configured with proper CORS settings for your domain.');
        }
        
        // Check if it's a network error
        if (uploadError.message && (uploadError.message.includes('NetworkError') || uploadError.message.includes('Failed to fetch'))) {
           alert('S3 upload failed due to network issues. Please check your connection and try again.');
        }
        
        throw uploadError;
      }
      
      // Step 4: Update questions with S3 URLs (only for new questions)
      const updatedQuestions = bulkQuestions.map((question, questionIndex) => {
        // For existing questions, return as-is without modifying images
        if (question.existing) {
          return question;
        }
        
        // For new questions, update with S3 URLs
        const updatedQuestion = { ...question };
        
        // Update question image URL
        if (question.questionImage && question.questionImageName) {
          const fileKey = `question-${questionIndex}`;
          const s3Url = s3UrlMap.get(fileKey);
          if (s3Url) {
            // Extract S3 key from full URL
            const s3Key = s3Url.replace('https://nextgenartifacts-dev.s3.ap-southeast-2.amazonaws.com/', '');
            updatedQuestion.questionImage = s3Key;
          }
        }
        
        // Update answer image URLs
        if (question.answerImages && question.answerImageNames) {
          updatedQuestion.answerImages = question.answerImages.map((answerImage, answerIndex) => {
            if (answerImage && question.answerImageNames[answerIndex]) {
              const fileKey = `question-${questionIndex}-answer-${answerIndex}`;
              const s3Url = s3UrlMap.get(fileKey);
              if (s3Url) {
                // Extract S3 key from full URL
                const s3Key = s3Url.replace('https://nextgenartifacts-dev.s3.ap-southeast-2.amazonaws.com/', '');
                return s3Key;
              }
              return answerImage;
            }
            return answerImage;
          });
        }
        
        return updatedQuestion;
      });
      
      // Step 5: Submit questions to backend with S3 URLs
      await submitQuestionsToBackend(updatedQuestions);
      
    } catch (error) {
      alert('Failed to submit questions. Please try again.');
    } finally {
      setIsBulkUploading(false);
      setBulkUploadProgress({});
    }
  };

  const submitQuestionsToBackend = async (questions) => {
    try {
      // Normalize ALL questions to backend format
      const normalizedQuestions = questions.map(question => {
        // Normalize correctAnswer to index string format
        let normalizedCorrectAnswer = question.correctAnswer;
        if (typeof question.correctAnswer === 'string') {
          // If it's a letter like "A" or "A,B", convert to index
          if (/^[A-Z](,[A-Z])*$/.test(question.correctAnswer)) {
            normalizedCorrectAnswer = question.correctAnswer
              .split(',')
              .map(letter => letter.trim().charCodeAt(0) - 65)
              .join(',');
          }
          // If already numeric like "0" or "0,2", keep as is
        } else if (typeof question.correctAnswer === 'number') {
          normalizedCorrectAnswer = String(question.correctAnswer);
        } else if (Array.isArray(question.correctAnswer)) {
          normalizedCorrectAnswer = question.correctAnswer.join(',');
        }

        // Normalize answerExplanations to array format
        let normalizedAnswerExplanations = [];
        if (question.answerExplanations) {
          if (Array.isArray(question.answerExplanations)) {
            // Already array, use as-is
            normalizedAnswerExplanations = question.answerExplanations;
          } else if (typeof question.answerExplanations === 'object') {
            // Convert object {A: "test1", B: "test2"} to array
            const numOptions = question.options?.length || 6;
            normalizedAnswerExplanations = Array(numOptions).fill('');
            Object.entries(question.answerExplanations).forEach(([key, value]) => {
              const index = key.charCodeAt(0) - 65; // A=0, B=1, etc.
              if (index >= 0 && index < numOptions) {
                normalizedAnswerExplanations[index] = value;
              }
            });
          }
        }

        // Process images - extract S3 keys from full URLs (handles both dev and prod)
        let normalizedQuestionImage = question.questionImage;
        if (question.questionImage) {
          // Handle virtual-hosted style URLs (bucket.s3.region.amazonaws.com)
          if (question.questionImage.includes('https://nextgenartifacts-dev.s3.ap-southeast-2.amazonaws.com/')) {
            const s3BaseUrl = 'https://nextgenartifacts-dev.s3.ap-southeast-2.amazonaws.com/';
            normalizedQuestionImage = question.questionImage.replace(s3BaseUrl, '').split('?')[0];
          } else if (question.questionImage.includes('https://nextgenartifacts-prod.s3.ap-southeast-2.amazonaws.com/')) {
            const s3BaseUrl = 'https://nextgenartifacts-prod.s3.ap-southeast-2.amazonaws.com/';
            normalizedQuestionImage = question.questionImage.replace(s3BaseUrl, '').split('?')[0];
          }
          // Handle path-style URLs (s3.region.amazonaws.com/bucket) - legacy
          else if (question.questionImage.includes('https://s3.ap-southeast-2.amazonaws.com/nextgenartifacts-dev/')) {
            const s3BaseUrl = 'https://s3.ap-southeast-2.amazonaws.com/nextgenartifacts-dev/';
            normalizedQuestionImage = question.questionImage.replace(s3BaseUrl, '').split('?')[0];
          } else if (question.questionImage.includes('https://s3.ap-southeast-2.amazonaws.com/nextgenartifacts-prod/')) {
            const s3BaseUrl = 'https://s3.ap-southeast-2.amazonaws.com/nextgenartifacts-prod/';
            normalizedQuestionImage = question.questionImage.replace(s3BaseUrl, '').split('?')[0];
          }
        }

        let normalizedAnswerImages = [];
        if (question.answerImages && Array.isArray(question.answerImages)) {
          normalizedAnswerImages = question.answerImages.map(img => {
            if (!img) return null;
            // Handle virtual-hosted style URLs (bucket.s3.region.amazonaws.com)
            if (img.includes('https://nextgenartifacts-dev.s3.ap-southeast-2.amazonaws.com/')) {
              const s3BaseUrl = 'https://nextgenartifacts-dev.s3.ap-southeast-2.amazonaws.com/';
              return img.replace(s3BaseUrl, '').split('?')[0];
            } else if (img.includes('https://nextgenartifacts-prod.s3.ap-southeast-2.amazonaws.com/')) {
              const s3BaseUrl = 'https://nextgenartifacts-prod.s3.ap-southeast-2.amazonaws.com/';
              return img.replace(s3BaseUrl, '').split('?')[0];
            }
            // Handle path-style URLs (s3.region.amazonaws.com/bucket) - legacy
            else if (img.includes('https://s3.ap-southeast-2.amazonaws.com/nextgenartifacts-dev/')) {
              const s3BaseUrl = 'https://s3.ap-southeast-2.amazonaws.com/nextgenartifacts-dev/';
              return img.replace(s3BaseUrl, '').split('?')[0];
            } else if (img.includes('https://s3.ap-southeast-2.amazonaws.com/nextgenartifacts-prod/')) {
              const s3BaseUrl = 'https://s3.ap-southeast-2.amazonaws.com/nextgenartifacts-prod/';
              return img.replace(s3BaseUrl, '').split('?')[0];
            }
            return img;
          });
        }

        // Return normalized question in backend format
        return {
          grade: question.grade || selectedExamSet?.grade,
          packageType: question.packageType || selectedExamSet?.package,
          questionTopic: question.questionTopic || selectedExamSet?.type,
          setNumber: question.setNumber || selectedExamSet?.setNumber,
          questionType: question.questionType,
          questionText: question.questionText,
          options: question.options || [],
          correctAnswer: normalizedCorrectAnswer,
          explanation: question.explanation || '',
          answerExplanations: normalizedAnswerExplanations,
          createdBy: question.createdBy || user?.email,
          id: question.id || Date.now().toString(),
          questionImage: normalizedQuestionImage,
          answerImages: normalizedAnswerImages
        };
      });

      // Prepare the payload for backend
      const payload = {
        examSet: selectedExamSet,
        questions: normalizedQuestions,
        deletedQuestions: deletedQuestions || [] // Include deleted question IDs
      };

     
       
      const response = await fetch(getFullUrl('exam/create-exam-bulk-questions'), {
      //  const response = await fetch('http://localhost:3001/dev/exam/create-exam-bulk-questions', {
      
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user?.session?.idToken.jwtToken}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit questions');
      }
      
      alert(`Successfully submitted ${questions.length} questions with images!`);
      
      // Reset everything after successful submission
      setBulkQuestions([]);
      setDeletedQuestions([]); // Reset deleted questions
      setSelectedExamSet(null);
      setQuestionForm({
        grade: '',
        packageType: '',
        questionTopic: '',
        setNumber: '',
        questionType: '',
        questionText: '',
        options: ['', '', '', '', ''],
        correctAnswer: '',
        explanation: '',
        answerExplanations: ['', '', '', '', ''],
        questionImage: null,
        answerImages: [null, null, null, null, null],
        questionImageName: '',
        answerImageNames: ['', '', '', '', '']
      });
      setCurrentStep(1);
      setEditingBulkQuestionIndex(null);
      
    } catch (error) {
      throw error;
    }
  };

  // Create exam set if not exists
  const createExamSetIfNotExists = async () => {
    try {
      const payload = {
        grade: questionForm.grade?.replace('GRADE#', ''),
        package: questionForm.packageType,
        type: questionForm.questionTopic,
        setNumber: questionForm.setNumber,
        title: `Set ${questionForm.setNumber}`,
        duration: 40,
        tags: [
          `Grade ${questionForm.grade?.replace('GRADE#', '')}`,
          questionForm.questionTopic,
          questionForm.packageType?.replace('PKG_', '')
        ]
      };
let idtoken = user?.session?.idToken;
   
      // Use fetch to handle ReadableStream properly
      const response = await fetch(getFullUrl('create-examset-if-not-exists'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user?.session?.idToken.jwtToken}`
        },
        body: JSON.stringify(payload)
      }).catch(fetchError => {
         throw fetchError;
      });

       
      // Check if response is valid
      if (!response) {
        throw new Error('No response received from API');
      }

      // Check response status
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Handle response body
      let responseBody = null;
      if (response && response.body && typeof response.body === 'string') {
        responseBody = JSON.parse(response.body); 
      } else if (response && response.body && response.body instanceof ReadableStream) {
         const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let result = '';
        
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          result += decoder.decode(value, { stream: true });
        }
        
        responseBody = JSON.parse(result);
      } else if (response) {
        responseBody = response; 
      }

      // Debug: Log the actual API response structure
    
      // Store and show API response 
      setApiResponse(responseBody);
      setShowApiResponse(true);
      
      

      if (responseBody && (responseBody.success || responseBody.statusCode === 201)) {
         // Re-check availability after creation
        await checkexamSetAvailability();
        // Keep response visible (no auto-hide)
      } else {
        // Keep error response visible (no auto-hide)
      }
    } catch (error) {
      // Store error response
      setApiResponse({
        success: false,
        error: error.message,
        message: 'Failed to create exam set due to network error'
      });
      setShowApiResponse(true);
      // Keep error response visible (no auto-hide)
    }
  };

  // Wizard navigation functions
  const nextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const isStepValid = (step) => {
    switch(step) {
      case 1:
        return questionForm.grade && questionForm.packageType && questionForm.questionTopic && questionForm.setNumber && (examSetAvailability || selectedExamSet);
      case 2:
        return questionForm.questionType && questionForm.questionText.trim();
      case 3:
        if (questionForm.questionType === 'MCQ') {
          return questionForm.options.filter(opt => opt.trim()).length >= 2 && questionForm.correctAnswer;
        } else if (questionForm.questionType === 'MULTI_SELECT') {
          return questionForm.options.filter(opt => opt.trim()).length >= 5 && questionForm.correctAnswer;
        } else if (questionForm.questionType === 'SHORT_ANSWER') {
          return questionForm.correctAnswer.trim();
        } else if (questionForm.questionType === 'LONG_WRITING') {
          return questionForm.correctAnswer.trim().length > 0;
        }
        return true;
      case 4:
        return questionForm.explanation.trim();
      default:
        return false;
    }
  };

  // Availability check now only happens on button click
  // Removed automatic check on form field changes

  if (loading) {
    return <div className="p-10 text-center">Loading...</div>;
  }

  // Simple reusable card
  const Card = ({ title, value, icon }) => (
    <div className="bg-white p-4 rounded shadow flex justify-between">
      <div>
        <p className="text-sm">{title}</p>
        <p className="text-xl font-bold">{value}</p>
      </div>
      {icon}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* HEADER */}
      <div className="bg-white p-4 flex justify-between shadow">
        <div className="flex items-center gap-2">
          <Shield />
          <h1 className="font-bold">Admin Panel</h1>
        </div>

        <button onClick={handleLogout}>
          <LogOut />
        </button>
      </div>

      {/* TABS */}
      <div className="flex border-b">
        <button
          onClick={() => setActiveTab('statistics')}
          className={`px-6 py-3 font-medium ${
            activeTab === 'statistics'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <BarChart3 className="w-4 h-4 mr-2" />
          Statistics
        </button>

        <button
          onClick={() => setActiveTab('questions')}
          className={`px-6 py-3 font-medium ${
            activeTab === 'questions'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <FileText className="w-4 h-4 mr-2" />
          Questions
        </button>

        <button
          onClick={() => setActiveTab('blogs')}
          className={`px-6 py-3 font-medium ${
            activeTab === 'blogs'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <FileText className="w-4 h-4 mr-2" />
          Blogs
        </button>
      </div>

      {/* CONTENT */}
      <div className="p-6">
        {/* STATISTICS TAB */}
        {activeTab === 'statistics' && (
          <div>
            {/* STATS CARDS */}
            <div className="grid grid-cols-4 gap-4 mb-8">
              <Card title="Users" value={stats.totalUsers} icon={<Users />} />
              <Card title="Exams" value={stats.totalExams} icon={<BookOpen />} />
              <Card title="Revenue" value={`$${stats.totalRevenue}`} icon={<DollarSign />} />
              <Card title="Active" value={stats.activeUsers} icon={<TrendingUp />} />
            </div>

            {/* RECENT USERS */}
            <div className="bg-white rounded shadow p-6">
              <h2 className="text-lg font-bold mb-4">Recent Users</h2>
              <div className="space-y-3">
                {recentUsers.map(u => (
                  <div key={u.id} className="flex items-center justify-between p-3 border-b">
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-gray-600" />
                      <div>
                        <p className="font-medium">{u.name}</p>
                        <p className="text-sm text-gray-500">{u.email}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded ${
                      u.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {u.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* QUESTIONS TAB */}
        {activeTab === 'questions' && (
          <div>
            {/* Selected Exam Set Heading */}
            {selectedExamSet && (
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg p-6 mb-6 shadow-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold mb-2">
                      {selectedExamSet.grade} • {selectedExamSet.package} • {selectedExamSet.type}
                    </h2>
                    <p className="text-blue-100 text-lg">
                      Set {selectedExamSet.setNumber} • {bulkQuestions.length} Questions Added
                    </p>
                    <div className="mt-3 flex items-center gap-4">
                      <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                        Max 40 Questions
                      </span>
                      <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                        {bulkQuestions.length}/40 Complete
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold">{bulkQuestions.length}</div>
                    <div className="text-blue-100 text-sm">Questions</div>
                  </div>
                </div>
              </div>
            )}

            {/* HEADER */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-bold">
                {!selectedExamSet ? 'Questions' : `Questions for Set ${selectedExamSet.setNumber}`}
              </h2>
              <div className="flex gap-2">
                {!selectedExamSet ? (
                  <button
                    onClick={() => {
                      setEditingBulkQuestionIndex(null);
                      resetQuestionForm(false);
                      setCurrentStep(1);
                      setShowCreateQuestion(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    <Plus className="w-4 h-4" />
                    Select Exam Set
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setEditingBulkQuestionIndex(null);
                      resetQuestionForm(true);
                      setCurrentStep(1);
                      setShowCreateQuestion(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    <Plus className="w-4 h-4" />
                    Add Question ({bulkQuestions.length}/40)
                  </button>
                )}
              </div>
            </div>

            {/* Selected Exam Set Info (Compact version) */}
            {selectedExamSet && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <div>
                      <p className="font-semibold text-green-800">Active Exam Set</p>
                      <p className="text-sm text-green-600">
                        Grade {selectedExamSet.grade} • {selectedExamSet.package} • {selectedExamSet.type} • Set {selectedExamSet.setNumber}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      if (bulkQuestions.length > 0) {
                        if (!window.confirm("Are you sure you want to change the exam set? You will lose any unsaved questions.")) {
                          return;
                        }
                      }
                      setSelectedExamSet(null);
                      setBulkQuestions([]);
                      setCurrentStep(1);
                      setEditingBulkQuestionIndex(null);
                    }}
                    className="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Change Exam Set
                  </button>
                </div>
              </div>
            )}

            {/* QUESTIONS LIST */}
            <div className="space-y-4">
              {!selectedExamSet ? (
                
                <div className="text-center py-12 bg-white rounded">
                  <p className="text-gray-500">Please select an exam set first to start creating questions</p>
                </div>
              ) : (
                <>
                
                  {bulkQuestions.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded">
                      <p className="text-gray-500">No questions created yet for this exam set</p>
                    </div>
                  ) : (
                    bulkQuestions.map((q, i) => (
                  <div key={q.id || i} className="bg-white p-4 rounded shadow">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold text-gray-800 text-sm">
                            Question {i + 1}
                          </span>
                          <span className="text-gray-300">|</span>
                          <span className={`px-2 py-1 text-xs rounded ${
                            q.existing ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'
                          }`}>
                            {q.questionType}
                            {q.existing && ' • Existing'}
                          </span>
                          {q.existing && (
                            <span className="text-xs text-gray-500">
                              Created by: {q.createdBy?.substring(0, 8)}...
                            </span>
                          )}
                        </div>
                        <p className="font-medium text-gray-900 mb-2 leading-relaxed" dangerouslySetInnerHTML={{ __html: q.questionText }} />
                        
                        {/* Show options for MCQ and MULTI_SELECT */}
                        {(q.questionType === 'MCQ' || q.questionType === 'MULTI_SELECT') && q.options && q.options.length > 0 && (
                          <div className="mb-2">
                            <p className="text-sm font-medium text-gray-700 mb-1">Options:</p>
                            <div className="space-y-1">
                              {q.options.map((option, optIndex) => {
                                const optionLetter = String.fromCharCode(65 + optIndex);
                                
                                // Normalize correctAnswer to handle both index and letter formats
                                const correctAnswerStr = String(q.correctAnswer || '');
                                const correctAnswers = q.questionType === 'MULTI_SELECT' 
                                  ? correctAnswerStr.split(',').map(a => a.trim()).filter(Boolean)
                                  : [correctAnswerStr].filter(Boolean);
                                
                                // Check if this option is correct (by letter or by index)
                                const isCorrect = correctAnswers.includes(optionLetter) || 
                                                 correctAnswers.includes(String(optIndex)) ||
                                                 correctAnswers.includes(option);
                               
                                
                                
                                return (
                                  <div key={optIndex} className="flex items-start gap-2 text-sm">
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      <span className={`font-medium ${
                                        isCorrect ? 'text-green-600' : 'text-gray-600'
                                      }`}>
                                        {String.fromCharCode(65 + optIndex)}.
                                      </span>
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex items-start gap-2">
                                        <span className="leading-relaxed" dangerouslySetInnerHTML={{ __html: option }} />
                                        {isCorrect && (
                                          <span className="text-green-600 text-xs font-medium">✓ Correct</span>
                                        )}
                                      </div>
                                      {/* Show answer image if exists */}
                                      {q.answerImages && q.answerImages[optIndex] && (
                                        <div className="mt-2">
                                          <img 
                                            src={q.answerImages[optIndex]} 
                                            alt={`Answer option ${optIndex + 1}`} 
                                            className="h-20 w-20 object-cover rounded border border-gray-200"
                                          />
                                          {q.answerImageNames && q.answerImageNames[optIndex] && (
                                            <div className="mt-1 text-xs text-gray-600 truncate w-24" title={q.answerImageNames[optIndex]}>
                                              📎 {q.answerImageNames[optIndex]}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        
                        {/* Show correct answer for short answer */}
                        {q.questionType === 'SHORT_ANSWER' && (
                          <div className="mb-2">
                            <p className="text-sm font-medium text-gray-700">Correct Answer:</p>
                            <p className="text-sm text-green-600">{q.correctAnswer}</p>
                          </div>
                        )}
                        
                        {/* Show model answer for long writing */}
                        {q.questionType === 'LONG_WRITING' && (
                          <div className="mb-2">
                            <p className="text-sm font-medium text-gray-700">Model Answer:</p>
                            <p className="text-sm text-green-600 whitespace-pre-wrap">{q.correctAnswer}</p>
                          </div>
                        )}
                        
                        {/* Show explanation if available */}
                        {q.explanation && (
                          <div className="mb-2">
                            <p className="text-sm font-medium text-gray-700">Explanation:</p>
                            <p className="text-sm text-gray-600">{q.explanation}</p>
                          </div>
                        )}
                        
                        {/* Show individual answer explanations for MCQ and MULTI_SELECT */}
                         {(q.questionType === 'MCQ' || q.questionType === 'MULTI_SELECT') && q.answerExplanations && (
                           <div className="mb-2">
                             <p className="text-sm font-medium text-gray-700 mb-2">Answer Explanations:</p>
                             <div className="space-y-2">
                               {q.options.map((option, optIndex) => {
                                 const optionKey = String.fromCharCode(65 + optIndex);
                                 const explanation = Array.isArray(q.answerExplanations) 
                                   ? q.answerExplanations[optIndex]
                                   : q.answerExplanations[optionKey];
                                  
                                 
                                 return (
                                  option.trim() && explanation && (
                                    <div key={optIndex} className="bg-gray-50 p-2 rounded border border-gray-200">
                                      <p className="text-xs font-medium text-gray-700">
                                        {optionKey}. {option}
                                      </p>
                                      <p className="text-xs text-gray-600 mt-1 leading-relaxed" dangerouslySetInnerHTML={{ __html: explanation }} />
                                    </div>
                                  )
                                );
                              })}
                            </div>
                          </div>
                        )}
                        
                        {/* Show question image if available */}
                        {q.questionImage && (
                          <div className="mb-2">
                            <p className="text-sm font-medium text-gray-700 mb-1">Question Image:</p>
                            <img 
                              src={q.questionImage} 
                              alt="Question" 
                              className="h-32 w-auto object-cover rounded border border-gray-200"
                            />
                            {q.questionImageName && (
                              <p className="text-xs text-gray-600 mt-1 truncate" title={q.questionImageName}>
                                📎 {q.questionImageName}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex gap-2 ml-4">
                        <button 
                          onClick={() => {
                            setQuestionForm(bulkQuestionToQuestionForm(q));
                            setEditingBulkQuestionIndex(i);
                            setCurrentStep(1);
                            setShowCreateQuestion(true);
                          }}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => {
                            if (showConfirmDialog(`Are you sure you want to delete this ${q.existing ? 'existing' : 'new'} question?`)) {
                              // Track deleted question for backend
                              if (q.existing && q.id) {
                                setDeletedQuestions(prev => [...prev, { id: q.id, deleted: true }]);
                              }
                              // Remove from UI (but keep in questions array for submission)
                              setBulkQuestions(prev => prev.filter((_, index) => index !== i));
                            }
                          }}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
                </>
              )}
            </div>

            {/* Bulk Submit Button */}
            {selectedExamSet && bulkQuestions.length > 0 && (
              <div className="mt-6 space-y-4">
                {/* Debug Payload Display */}
                <div className="bg-gray-900 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="text-sm font-medium text-gray-300">Debug: Backend Payload Preview</h4>
                    <button
                      onClick={() => {
                        // Generate preview payload
                        const previewPayload = {
                          examSet: selectedExamSet,
                          questions: bulkQuestions.map(q => ({
                            id: q.id,
                            questionType: q.questionType,
                            questionText: q.questionText?.substring(0, 50) + '...',
                            correctAnswer: q.correctAnswer,
                            answerExplanations: q.answerExplanations,
                            questionImage: q.questionImage ? '✓ Present' : '✗ None',
                            answerImages: q.answerImages?.map(img => img ? '✓' : '✗') || [],
                            existing: q.existing || false
                          })),
                          deletedQuestions: deletedQuestions || []
                        };
                        navigator.clipboard.writeText(JSON.stringify(previewPayload, null, 2));
                        alert('Payload copied to clipboard!');
                      }}
                      className="text-xs bg-gray-700 text-gray-300 px-2 py-1 rounded hover:bg-gray-600"
                    >
                      Copy to Clipboard
                    </button>
                  </div>
                  <textarea
                    readOnly
                    value={JSON.stringify({
                      examSet: selectedExamSet,
                      questions: bulkQuestions.map(q => ({
                        id: q.id,
                        questionType: q.questionType,
                        questionText: q.questionText?.substring(0, 50) + '...',
                        correctAnswer: q.correctAnswer,
                        answerExplanations: q.answerExplanations,
                        questionImage: q.questionImage ? '✓ Present' : '✗ None',
                        answerImages: q.answerImages?.map(img => img ? '✓' : '✗') || [],
                        existing: q.existing || false
                      })),
                      deletedQuestions: deletedQuestions || []
                    }, null, 2)}
                    rows={10}
                    className="w-full bg-gray-800 text-green-400 font-mono text-xs p-2 rounded border border-gray-700 resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Questions: {bulkQuestions.length} | Deleted: {deletedQuestions.length}
                  </p>
                </div>

                {/* Upload Progress */}
                {isBulkUploading && Object.keys(bulkUploadProgress).length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-800 mb-2">Uploading Images to S3:</h4>
                    {Object.entries(bulkUploadProgress).map(([fileName, progress]) => (
                      <div key={fileName} className="mb-2">
                        <div className="flex justify-between text-xs text-gray-600 mb-1">
                          <span className="truncate max-w-xs">{fileName}</span>
                          <span>{progress.toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="flex justify-end">
                  <button
                    onClick={handleBulkSubmit}
                    disabled={isBulkUploading}
                    className={`px-6 py-2 rounded font-medium transition-colors ${
                      isBulkUploading 
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    {isBulkUploading ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Uploading...
                      </span>
                    ) : (
                      `Submit All Questions (${bulkQuestions.length})`
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* QUESTION CREATION WIZARD */}
        {showCreateQuestion && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {!selectedExamSet
                    ? 'Select Exam Set'
                    : editingBulkQuestionIndex !== null
                      ? 'Edit Question'
                      : 'Add Question'}
                </h2>
                <button
                  onClick={() => {
                    setShowCreateQuestion(false);
                    setCurrentStep(1);
                    setEditingBulkQuestionIndex(null);
                    resetQuestionForm(true);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Progress Steps - Only show for question creation */}
              {selectedExamSet && (
                <div className="flex items-center justify-between mb-8">
                  {[1, 2, 3, 4].map((step) => (
                    <div key={step} className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        currentStep >= step ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
                      }`}>
                        {step}
                      </div>
                      {step < 4 && (
                        <div className={`w-16 h-1 ${
                          currentStep > step ? 'bg-blue-600' : 'bg-gray-300'
                        }`} />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Exam Set Selection (Phase 1) */}
              {!selectedExamSet && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Exam Set for Questions</h3>
                  
                  {/* Availability Status */}
                  {examSetAvailability && (
                    <div className={`p-4 rounded-lg border ${
                      examSetAvailability.available 
                        ? 'bg-green-50 border-green-200' 
                        : 'bg-red-50 border-red-200'
                    }`}>
                      <div className="flex items-center">
                        <div className={`w-4 h-4 rounded-full mr-3 ${
                          examSetAvailability.available 
                            ? 'bg-green-500' 
                            : 'bg-red-500'
                        }`}>
                          {examSetAvailability.available ? (
                            <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 00-1.414 1.414l-8 8a1 1 0 001.414-1.414L16.707 5.293z" />
                            </svg>
                          ) : (
                            <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 1.414L10 10l4.293 4.293a1 1 0 001.414-1.414L4.293 4.293z" />
                            </svg>
                          )}
                        </div>
                        <div>
                          <p className={`font-medium ${
                            examSetAvailability.available 
                              ? 'text-green-800' 
                              : 'text-red-800'
                          }`}>
                            {examSetAvailability.message}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Grade Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Grade *</label>
                    <select
                      value={questionForm.grade}
                      onChange={(e) => handleQuestionFormChange('grade', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Grade</option>
                      <option value="GRADE#3">Grade 3</option>
                      <option value="GRADE#5">Grade 5</option>
                      <option value="GRADE#7">Grade 7</option>
                      <option value="GRADE#9">Grade 9</option>
                    </select>
                  </div>

                  {/* Package Type Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Package Type *</label>
                    <select
                      value={questionForm.packageType}
                      onChange={(e) => handleQuestionFormChange('packageType', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Package Type</option>
                      <option value="PKG_BASIC">Basic Package</option>
                      <option value="PKG_STANDARD">Standard Package</option>
                      <option value="PKG_PREMIUM">Premium Package</option>
                    </select>
                  </div>

                  {/* Exam Type Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Exam Type *</label>
                    <select
                      value={questionForm.questionTopic}
                      onChange={(e) => handleQuestionFormChange('questionTopic', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Exam Type</option>
                      <option value="READING">Reading</option>
                      <option value="WRITING">Writing</option>
                      <option value="NUMERACY">Numeracy</option>
                      <option value="LANGUAGE">Language</option> 
                    </select>
                  </div>

                  {/* Exam Set Number Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Exam Set Number *</label>
                    <select
                      value={questionForm.setNumber}
                      onChange={(e) => handleQuestionFormChange('setNumber', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select Set Number</option>
                      {Array.from({ 
  length: questionForm.packageType === 'PKG_PREMIUM' ? 12 : 
         questionForm.packageType === 'PKG_STANDARD' ? 8 : 
         questionForm.packageType === 'PKG_BASIC' ? 3 :
         questionForm.packageType === 'MULTI_SELECT' ? 8 : 3
 }, (_, i) => i + 1).map(setNum => (
                        <option key={setNum} value={setNum}>
                          Set {setNum}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Select Button */}
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={checkexamSetAvailability}
                      disabled={!questionForm.grade || !questionForm.packageType || !questionForm.questionTopic || !questionForm.setNumber}
                      className={`px-4 py-2 rounded-lg ${
                        questionForm.grade && questionForm.packageType && questionForm.questionTopic && questionForm.setNumber
                          ? 'bg-orange-600 text-white hover:bg-orange-700'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      Check Availability
                    </button>
                    <button
                      onClick={examSetAvailability && examSetAvailability.examSet ? handleCreateQuestion : createExamSetIfNotExists}
                      disabled={!questionForm.grade || !questionForm.packageType || !questionForm.questionTopic || !questionForm.setNumber || !examSetAvailability}
                      className={`px-6 py-2 rounded-lg ${
                        questionForm.grade && questionForm.packageType && questionForm.questionTopic && questionForm.setNumber && examSetAvailability
                          ? examSetAvailability.examSet
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-green-600 text-white hover:bg-green-700'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      {examSetAvailability && examSetAvailability.examSet ? 'Select This Exam Set' : 'Create Exam Set'}
                    </button>
                  </div>

                  {/* API Response Display */}
                  {showApiResponse && apiResponse && (
                    <div className={`mt-4 p-4 rounded-lg border ${
                      apiResponse?.success || apiResponse.statusCode === 201
                        ? 'bg-green-50 border-green-200 text-green-800' 
                        : 'bg-red-50 border-red-200 text-red-800'
                    }`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className={`font-semibold mb-2 ${
                            apiResponse?.success || apiResponse.statusCode === 201
                              ? 'text-green-800' : 'text-red-800'
                          }`}>
                            {apiResponse?.success || apiResponse.statusCode === 201 ? '✅ Success' : '❌ Error'}
                          </h4>
                          <p className="text-sm mb-2">
                            {(apiResponse?.success || apiResponse.statusCode === 201) 
                              ? (apiResponse?.message || 'Exam set created successfully')
                              : (apiResponse?.message || 'Failed to create Exam set')
                            }
                          </p>
                        </div>
                        
                        <button
                          onClick={() => {
                            setShowApiResponse(false);
                          }}
                          className="ml-4 text-gray-500 hover:text-gray-700"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Question Creation Steps (Phase 2) - Only show when NEP set is selected */}
              {selectedExamSet && (
                <>
                  {/* Step 1: Question Type & Text */}
                  {currentStep === 1 && (
                    <div className="space-y-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Step 1: Question Type & Text</h3>
                      
                      {/* Question Type Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Question Type *</label>
                        <select
                          value={questionForm.questionType}
                          onChange={(e) => handleQuestionFormChange('questionType', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select Question Type</option>
                          <option value="MCQ">Multiple Choice</option>
                          <option value="MULTI_SELECT">Multi Select (Multiples of 3)</option>
                          <option value="SHORT_ANSWER">Short Answer</option>
                          <option value="LONG_WRITING">Long Writing</option>
                        </select>
                      </div>

                      {/* Question Text */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Question Text *</label>
                        <RichTextEditor
                          value={questionForm.questionText}
                          onChange={(value) => handleQuestionFormChange('questionText', value)}
                          placeholder="Enter your question here..."
                          rows={4}
                        />
                      </div>

                      {/* Question Image Upload */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Question Image (Optional)</label>
                        <div className="flex items-center space-x-4">
                          {questionForm.questionImage ? (
                            <div className="relative">
                              <img 
                                src={questionForm.questionImage} 
                                alt="Question" 
                                className="h-32 w-32 object-cover rounded-lg border border-gray-200"
                              />
                              {questionForm.questionImageName && (
                                <div className="mt-1 text-xs text-gray-600 truncate w-32" title={questionForm.questionImageName}>
                                  📎 {questionForm.questionImageName}
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={removeQuestionImage}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                              >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ) : (
                            <label className="flex flex-col items-center justify-center h-32 w-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleQuestionImageUpload}
                                className="hidden"
                              />
                              <div className="text-center">
                                <div className="text-gray-400">
                                  <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                  </svg>
                                </div>
                                <p className="text-xs text-gray-500">Upload Question Image</p>
                              </div>
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 2: Options & Correct Answer */}
                   {currentStep === 2 && (
                    <div className="space-y-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Step 2: Options & Correct Answer</h3>
                      
                      {/* Options (for MCQ and MULTI_SELECT) */}
                      {(questionForm.questionType === 'MCQ' || questionForm.questionType === 'MULTI_SELECT') && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Options *</label>
                          <div className="space-y-4">
                            {questionForm.options.map((option, index) => (
                              <div key={index} className="flex items-start space-x-4">
                                <div className="flex-1">
                                  <RichTextEditor
                                    value={option}
                                    onChange={(value) => handleOptionChange(index, value)}
                                    placeholder={questionForm.questionType === 'MULTI_SELECT' ? 
                                      (index === 0 ? '12' : 
                                       index === 1 ? '15' : 
                                       index === 2 ? '18' : 
                                       index === 3 ? '20' : '24') : 
                                      `Option ${index + 1}`
                                    }
                                    rows={2}
                                  />
                                </div>
                                <div className="flex flex-col items-center">
                                  {questionForm.answerImages[index] ? (
                                    <div className="relative">
                                      <img 
                                        src={questionForm.answerImages[index]} 
                                        alt={`Option ${index + 1}`} 
                                        className="h-16 w-16 object-cover rounded-lg border border-gray-200"
                                      />
                                      {questionForm.answerImageNames[index] && (
                                        <div className="mt-1 text-xs text-gray-600 truncate w-16" title={questionForm.answerImageNames[index]}>
                                          📎 {questionForm.answerImageNames[index]}
                                        </div>
                                      )}
                                      <button
                                        type="button"
                                        onClick={() => removeAnswerImage(index)}
                                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                      >
                                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                      </button>
                                    </div>
                                  ) : (
                                    <label className="flex flex-col items-center justify-center h-16 w-16 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400">
                                      <input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => handleAnswerImageUpload(index, e)}
                                        className="hidden"
                                      />
                                      <div className="text-center">
                                        <div className="text-gray-400">
                                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                          </svg>
                                        </div>
                                        <p className="text-xs text-gray-500">Image</p>
                                      </div>
                                    </label>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Correct Answer */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Correct Answer *</label>
                        {questionForm.questionType === 'MCQ' ? (
                          <select
                            value={questionForm.correctAnswer}
                            onChange={(e) => handleQuestionFormChange('correctAnswer', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Select Correct Option</option>
                            {questionForm.options.map((option, index) => (
                              option.trim() && (
                                <option key={index} value={index}>
                                  Option {index + 1}: {option}
                                </option>
                              )
                            ))}
                          </select>
                        ) : questionForm.questionType === 'MULTI_SELECT' ? (
                          <div className="space-y-2">
                            <p className="text-sm text-gray-600 mb-2">Select all correct answers (multiples of 3):</p>
                            {questionForm.options.map((option, index) => (
                              option.trim() && (
                                <label key={index} className="flex items-center space-x-2">
                                  <input
                                    type="checkbox"
                                    checked={questionForm.correctAnswer && questionForm.correctAnswer.includes(index.toString())}
                                    onChange={(e) => {
                                      const currentAnswers = questionForm.correctAnswer ? questionForm.correctAnswer.split(',') : [];
                                      if (e.target.checked) {
                                        currentAnswers.push(index.toString());
                                      } else {
                                        const answerIndex = currentAnswers.indexOf(index.toString());
                                        if (answerIndex > -1) {
                                          currentAnswers.splice(answerIndex, 1);
                                        }
                                      }
                                      handleQuestionFormChange('correctAnswer', currentAnswers.join(','));
                                    }}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                  />
                                  <span className="text-sm">
                                    Option {index + 1}: {option}
                                  </span>
                                </label>
                              )
                            ))}
                          </div>
                        ) : questionForm.questionType === 'LONG_WRITING' ? (
                          <RichTextEditor
                            value={questionForm.correctAnswer}
                            onChange={(value) => handleQuestionFormChange('correctAnswer', value)}
                            rows={6}
                            placeholder="Enter sample/model answer for long writing question..."
                          />
                        ) : (
                          <input
                            type="text"
                            value={questionForm.correctAnswer}
                            onChange={(e) => handleQuestionFormChange('correctAnswer', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter correct answer"
                          />
                        )}
                      </div>
                    </div>
                  )}

                  {/* Step 3: Explanation */}
                  {currentStep === 3 && (
                    <div className="space-y-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Step 3: Explanation</h3>
                      
                      {/* Explanation */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Explanation</label>
                        <RichTextEditor
                          value={questionForm.explanation}
                          onChange={(value) => handleQuestionFormChange('explanation', value)}
                          placeholder="Optional explanation for the answer..."
                          rows={4}
                        />
                      </div>

                      {/* Review */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-medium text-gray-900 mb-2">Review Your Question</h4>
                        <div className="space-y-2 text-sm">
                          <p><strong>NEP Set:</strong> {selectedExamSet.grade} • {selectedExamSet.package} • {selectedExamSet.type} • Set {selectedExamSet.setNumber}</p>
                          <p><strong>Question Type:</strong> {questionForm.questionType}</p>
                          <p><strong>Question:</strong> {questionForm.questionText}</p>
                          {(questionForm.questionType === 'MCQ' || questionForm.questionType === 'MULTI_SELECT') && (
                            <p><strong>Options:</strong> {questionForm.options.filter(opt => opt.trim()).join(', ')}</p>
                          )}
                          <p><strong>Correct Answer:</strong> {questionForm.questionType === 'MCQ' ? `Option ${parseInt(questionForm.correctAnswer) + 1}: ${questionForm.options[parseInt(questionForm.correctAnswer)] || 'Not selected'}` : questionForm.questionType === 'MULTI_SELECT' ? questionForm.correctAnswer ? questionForm.correctAnswer.split(',').map(idx => `Option ${parseInt(idx) + 1}: ${questionForm.options[parseInt(idx)] || 'Not selected'}`).join(', ') : 'No correct answers selected' : questionForm.correctAnswer}</p>
                          
                          {/* Individual Answer Explanations for MCQ and MULTI_SELECT */}
                          {(questionForm.questionType === 'MCQ' || questionForm.questionType === 'MULTI_SELECT') && questionForm.answerExplanations && questionForm.answerExplanations.length > 0 && (
                            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                              <h6 className="font-medium text-gray-900 mb-2">Answer Explanations</h6>
                              {questionForm.options.map((option, index) => (
                                option.trim() && questionForm.answerExplanations[index] && (
                                  <div key={index} className="mb-2 p-2 bg-white rounded border border-gray-100">
                                    <p className="text-sm">
                                      <strong>Option {index + 1}:</strong> {option}
                                    </p>
                                    <p className="text-xs text-gray-600 mt-1">
                                      {questionForm.answerExplanations[index]}
                                    </p>
                                  </div>
                                )
                              ))}
                            </div>
                          )}
                          
                          {/* Individual Answer Explanations for MCQ and MULTI_SELECT */}
                          {(questionForm.questionType === 'MCQ' || questionForm.questionType === 'MULTI_SELECT') && (
                            <div className="space-y-4 mt-4">
                              <h5 className="font-medium text-gray-900">Answer Explanations *</h5>
                              {questionForm.options.map((option, index) => (
                                option.trim() && (
                                  <div key={index} className="bg-white p-3 rounded border border-gray-200">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Option {index + 1}: {option}
                                    </label>
                                    <RichTextEditor
                                      key={`explanation-${index}`}
                                      value={questionForm.answerExplanations?.[index] || ''}
                                      onChange={(value) => handleAnswerExplanationChange(index, value)}
                                      rows={2}
                                      placeholder={`Explain why Option ${index + 1} is ${questionForm.questionType === 'MULTI_SELECT' && questionForm.correctAnswer && questionForm.correctAnswer.split(',').includes(index.toString()) ? 'correct' : questionForm.questionType === 'MCQ' && parseInt(questionForm.correctAnswer) === index ? 'correct' : 'incorrect'}...`}
                                    />
                                  </div>
                                )
                              ))}
                            </div>
                          )}
                          
                          {/* General explanation field */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">General Explanation</label>
                            <textarea
                              value={questionForm.explanation || ''}
                              onChange={(e) => handleQuestionFormChange('explanation', e.target.value)}
                              rows={4}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Optional general explanation for the question..."
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Navigation Buttons */}
                  <div className="flex justify-between mt-8">
                    <button
                      onClick={prevStep}
                      disabled={currentStep === 1}
                      className={`px-4 py-2 rounded-lg ${
                        currentStep === 1 
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                          : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Previous
                    </button>

                    {currentStep < 3 ? (
                      <button
                        onClick={nextStep}
                        disabled={!isStepValid(currentStep)}
                        className={`px-4 py-2 rounded-lg ${
                          isStepValid(currentStep)
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        Next
                      </button>
                    ) : (
                      <button
                        onClick={handleCreateQuestion}
                        disabled={!isStepValid(3)}
                        className={`px-4 py-2 rounded-lg ${
                          isStepValid(3)
                            ? 'bg-green-600 text-white hover:bg-green-700'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                      >
                        {editingBulkQuestionIndex !== null
                          ? 'Save Question'
                          : `Add to Bulk (${bulkQuestions.length + 1}/40)`}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* BLOGS TAB */}
        {activeTab === 'blogs' && (
          <div>
            {showBlogEditor ? (
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold">
                    {editingBlog ? 'Edit Blog Post' : 'Create Blog Post'}
                  </h2>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowPreview(!showPreview)}
                      className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
                    >
                      <Eye className="w-4 h-4" />
                      {showPreview ? 'Edit' : 'Preview'}
                    </button>
                    <button
                      onClick={saveBlog}
                      className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                    >
                      <Save className="w-4 h-4" />
                      Save
                    </button>
                    <button
                      onClick={() => setShowBlogEditor(false)}
                      className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                </div>

                {showPreview ? (
                  <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                    <div className="mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Blog Preview</h3>
                      <p className="text-sm text-gray-600">This is how your blog post will appear to users</p>
                    </div>
                    
                    {/* Preview Content */}
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                      {/* Featured Image Preview */}
                      {blogForm.featuredImage && (
                        <div className="aspect-w-16 aspect-h-9">
                          <img
                            src={blogForm.featuredImage}
                            alt={blogForm.title || 'Featured image'}
                            className="w-full h-64 object-cover"
                          />
                        </div>
                      )}

                      <div className="p-8">
                        {/* Tags Preview */}
                        {blogForm.tags && blogForm.tags.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-4">
                            {(Array.isArray(blogForm.tags) ? blogForm.tags : blogForm.tags.split(',')).slice(0, 3).map((tag, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                              >
                                <Tag className="w-3 h-3 mr-1" />
                                {tag.trim()}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Title Preview */}
                        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                          {blogForm.title || 'Blog Title'}
                        </h1>

                        {/* Meta Information Preview */}
                        <div className="flex flex-wrap items-center gap-4 text-gray-600 mb-6">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            <span>{user?.email || 'Author'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>{new Date().toLocaleDateString()}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            <span>{Math.ceil((blogForm.content?.replace(/<[^>]*>/g, '').split(' ').length || 0) / 200)} min read</span>
                          </div>
                        </div>

                        {/* Content Preview */}
                        <div className="prose prose-lg max-w-none">
                          {blogForm.content ? (
                            <div dangerouslySetInnerHTML={{ __html: blogForm.content }} />
                          ) : (
                            <p className="text-gray-500 italic">Blog content will appear here...</p>
                          )}
                        </div>

                        {/* SEO Preview Info */}
                        <div className="mt-8 pt-8 border-t border-gray-200">
                          <h4 className="text-sm font-semibold text-gray-900 mb-2">SEO Preview</h4>
                          <div className="bg-gray-50 p-4 rounded text-sm">
                            <div className="text-blue-600 text-lg mb-1">
                              {blogForm.metaTitle || blogForm.title || 'Blog Title'}
                            </div>
                            <div className="text-green-600 text-sm mb-2">
                              {window.location.origin}/blog/{blogForm.slug || 'url-slug'}
                            </div>
                            <div className="text-gray-600">
                              {blogForm.metaDescription || blogForm.excerpt || 'Blog description will appear here...'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Title
                        </label>
                        <input
                          type="text"
                          value={blogForm.title}
                          onChange={(e) => handleBlogTitleChange(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="Enter blog title"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          URL Slug
                        </label>
                        <input
                          type="text"
                          value={blogForm.slug}
                          onChange={(e) => setBlogForm(prev => ({ ...prev, slug: e.target.value }))}
                          disabled={editingBlog !== null}
                          className={`w-full px-3 py-2 border rounded-lg ${
                            editingBlog 
                              ? 'border-gray-200 bg-gray-50 text-gray-600 cursor-not-allowed' 
                              : 'border-gray-300 focus:ring-2 focus:ring-blue-500'
                          }`}
                          placeholder="url-friendly-slug"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Content
                        </label>
                        <RichTextEditor
                          value={blogForm.content}
                          onChange={handleBlogContentChange}
                          placeholder="Write your blog content here..."
                        />
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Status
                        </label>
                        <select
                          value={blogForm.status}
                          onChange={(e) => setBlogForm(prev => ({ ...prev, status: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="draft">Draft</option>
                          <option value="published">Published</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Tags (comma-separated)
                        </label>
                        <input
                          type="text"
                          value={blogForm.tags}
                          onChange={(e) => setBlogForm(prev => ({ ...prev, tags: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="tag1, tag2, tag3"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Featured Image
                        </label>
                        {blogForm.featuredImage ? (
                          <div className="relative">
                            <img
                              src={blogForm.featuredImage}
                              alt="Featured"
                              className="w-full h-48 object-cover rounded-lg"
                            />
                            <button
                              onClick={removeBlogImage}
                              className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full hover:bg-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleBlogImageUpload}
                              className="hidden"
                              id="blogFeaturedImage"
                            />
                            <label
                              htmlFor="blogFeaturedImage"
                              className="cursor-pointer flex flex-col items-center"
                            >
                              <Plus className="w-6 h-6 text-gray-400 mb-2" />
                              <span className="text-sm text-gray-500">Add featured image</span>
                            </label>
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          SEO Meta Title
                        </label>
                        <input
                          type="text"
                          value={blogForm.metaTitle}
                          onChange={(e) => setBlogForm(prev => ({ ...prev, metaTitle: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="SEO title (60 chars max)"
                          maxLength={60}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          SEO Meta Description
                        </label>
                        <textarea
                          value={blogForm.metaDescription}
                          onChange={(e) => setBlogForm(prev => ({ ...prev, metaDescription: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="SEO description (160 chars max)"
                          rows={3}
                          maxLength={160}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-lg">
                <div className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold">Blog Posts</h2>
                    <button
                      onClick={() => {
                        resetBlogForm();
                        setShowBlogEditor(true);
                      }}
                      className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                    >
                      <Plus className="w-4 h-4" />
                      New Blog Post
                    </button>
                  </div>
                  {!Array.isArray(blogs) || blogs.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>No blog posts yet. Create your first blog post!</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {blogs.map((blog) => (
                        <div key={blog.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-gray-900">{blog.title}</h3>
                              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  {new Date(blog.createdAt).toLocaleDateString()}
                                </span>
                                <span className="flex items-center gap-1">
                                  <User className="w-4 h-4" />
                                  {blog.author}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Tag className="w-4 h-4" />
                                  {blog.status}
                                </span>
                              </div>
                              {blog.excerpt && (
                                <div className="mt-2 text-gray-600 line-clamp-2 prose prose-sm max-w-none">
                                  <div dangerouslySetInnerHTML={{ __html: blog.excerpt }} />
                                </div>
                              )}
                            </div>
                            <div className="flex gap-2 ml-4">
                              <button
                                onClick={() => editBlog(blog)}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                <Eye className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => deleteBlog(blog.slug)}
                                className="text-red-600 hover:text-red-800"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;